import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Icons } from "@/assets";
import { cx } from "@/utils/cx";

interface UomOption {
    label: string;
    value: string;
}

interface InputWithUomFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "onBlur"> {
    name: string;
    label: string;
    value?: string;
    uomValue?: string;
    uomOptions: UomOption[];
    onChange?: (val: string) => void;
    onBlur?: (val: string) => void;
    onUomChange?: (val: string) => void;
    error?: string;
    required?: boolean;
    uomPlaceholder?: string;
}

export const InputWithUomField = forwardRef<HTMLInputElement, InputWithUomFieldProps>(({
    name,
    label,
    value,
    uomValue,
    uomOptions,
    onChange,
    onBlur,
    onUomChange,
    error,
    required,
    disabled,
    className,
    uomPlaceholder = "UOM",
    ...props
}, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const selectedOption = uomOptions.find((option) => String(option.value) === String(uomValue));
    const selectedLabel = selectedOption?.label || uomPlaceholder;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleUomSelect = (nextValue: string) => {
        onUomChange?.(nextValue);
        setIsOpen(false);
    };

    return (
        <div ref={wrapperRef} className="flex flex-col gap-2.5 w-full relative">
            <label htmlFor={name} className="text-base text-gray-90">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            <div
                className={cx(
                    "relative w-full rounded-[10px] p-[1.5px] transition-all duration-200 ease-in-out group",
                    error
                        ? "bg-[#ff424f]"
                        : "bg-gray-30 focus-within:bg-gradient-to-r focus-within:from-[#00B4EA] focus-within:to-[#8A00FF]",
                    disabled && "opacity-60 cursor-not-allowed bg-gray-200"
                )}
            >
                <div className="relative flex items-center bg-white rounded-[8.5px] overflow-visible">
                    <input
                        ref={ref}
                        id={name}
                        name={name}
                        value={value || ""}
                        disabled={disabled}
                        onChange={(event) => onChange?.(event.target.value)}
                        onBlur={(event) => onBlur?.(event.target.value)}
                        className={cx(
                            "w-full h-[51px] pl-4 pr-[104px] text-gray-900 text-sm placeholder:text-gray-400 outline-none bg-transparent",
                            disabled && "cursor-not-allowed bg-gray-50",
                            className
                        )}
                        {...props}
                    />

                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => setIsOpen((open) => !open)}
                        className={cx(
                            "absolute right-2 top-1/2 -translate-y-1/2 h-[37px] min-w-[84px] max-w-[112px] px-3",
                            "flex items-center justify-between gap-2 rounded-[8px] border border-gray-30 bg-gray-10",
                            "text-sm text-gray-90 outline-none transition-colors",
                            disabled ? "cursor-not-allowed" : "hover:border-gray-400"
                        )}
                    >
                        <span className="truncate">{selectedLabel}</span>
                        <Icons.ChevronDownIcon className={cx("w-4 h-4 shrink-0 text-gray-500 transition-transform", isOpen && "rotate-180")} />
                    </button>

                    {isOpen && !disabled && (
                        <div className="absolute top-[46px] right-2 min-w-[112px] max-w-[180px] bg-white border border-gray-200 rounded-[8px] shadow-lg max-h-[200px] overflow-y-auto z-50">
                            {uomOptions.length > 0 ? (
                                uomOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => handleUomSelect(option.value)}
                                        className={cx(
                                            "w-full px-3 py-2 text-left text-sm hover:bg-gray-50",
                                            String(option.value) === String(uomValue) ? "text-[#0a68ff] font-medium" : "text-gray-700"
                                        )}
                                    >
                                        {option.label}
                                    </button>
                                ))
                            ) : (
                                <div className="px-3 py-2 text-sm text-gray-400">No options found</div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <p className="text-xs text-error-60 font-medium">{error}</p>
            )}
        </div>
    );
});

InputWithUomField.displayName = "InputWithUomField";
